import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import PropTypes from "prop-types"
import {Stack} from "@mui/material"
import {RoutineService} from "src/services/routine-service"
import {dispatchSnackbar} from "src/v2/components/SnackBar/snackbar-bus"
import RoutineExecutionForm from "src/v2/components/Workflow/routine-execution-form"
import WorkflowExecutionsTable from "src/v2/components/Workflow/workflow-executions-table"
import {shouldPollWorkflows} from "src/v2/components/Workflow/workflow-execution"
import {normalizeCalculationExecution} from "../calculation-execution"
import usePaginatedExecutions from "../hooks/use-paginated-executions"
import {
    calculationRoutineDefinitions,
    executeCalculationRoutine,
    getRoutineErrorMessage,
} from "../routine-definitions"

const CalculationsTab = ({active, onLoadingChange}) => {
    const service = useMemo(() => new RoutineService(), [])
    const [actionLoading, setActionLoading] = useState(false)
    const lastLoadError = useRef(null)
    const fetchPage = useCallback(
        ({page, pageSize}) => service.listCalculationExecutions({page, pageSize}),
        [service]
    )
    const executions = usePaginatedExecutions({
        fetchPage,
        normalizeItem: normalizeCalculationExecution,
        active,
    })

    useEffect(() => {
        const message = executions.error ? "Erro ao carregar execuções de cálculos" : null
        if (message && message !== lastLoadError.current) dispatchSnackbar(message, "error")
        lastLoadError.current = message
    }, [executions.error])

    useEffect(() => () => onLoadingChange(false), [onLoadingChange])

    useEffect(() => {
        if (!active || !shouldPollWorkflows(executions.rows, false, document.visibilityState)) {
            return undefined
        }
        const refresh = () => {
            if (document.visibilityState === "visible") executions.load({silent: true})
        }
        const interval = window.setInterval(refresh, 5000)
        const onVisibilityChange = () => refresh()
        document.addEventListener("visibilitychange", onVisibilityChange)
        return () => {
            window.clearInterval(interval)
            document.removeEventListener("visibilitychange", onVisibilityChange)
        }
    }, [active, executions.load, executions.rows])

    const runAction = async (action, successMessage) => {
        setActionLoading(true)
        onLoadingChange(true)
        try {
            await action()
            dispatchSnackbar(successMessage, "success")
            await executions.load({silent: true})
        } catch (error) {
            dispatchSnackbar(getRoutineErrorMessage(error), "error")
        } finally {
            setActionLoading(false)
            onLoadingChange(false)
        }
    }

    const submit = (definition, values) => runAction(
        () => executeCalculationRoutine(service, definition, values),
        definition.value === "TratamentoRiskFree" ? "Risk Free V2 iniciado" : "Cálculo iniciado"
    )

    return (
        <Stack spacing={3} sx={{pt: 3}}>
            <RoutineExecutionForm
                routines={calculationRoutineDefinitions}
                loading={actionLoading}
                onSubmit={submit}
                onRebuild={() => runAction(() => service.startRebuild(), "Rebuild index iniciado")}
                onValidationError={(message) => dispatchSnackbar(message, "warning")}
            />
            <WorkflowExecutionsTable
                rows={executions.rows}
                total={executions.total}
                page={executions.page}
                pageSize={executions.pageSize}
                loading={executions.loading}
                resetKey="calculations"
                onPageChange={executions.changePage}
                onRefresh={() => executions.load()}
            />
        </Stack>
    )
}

CalculationsTab.propTypes = {
    active: PropTypes.bool,
    onLoadingChange: PropTypes.func.isRequired,
}

export default CalculationsTab
